import Facade from '@/Facade'
import GameSignals from '@/signals/GameSignals'
import { applyMaxWinCapToSpinData } from '@/states/round/MaxWinCap'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'
import { IFolder } from '@modules/src/interfaces/Ifolder'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import { DebugModule } from '@modules/src/modules/DebugModule'
import * as PIXI from 'pixi.js'

export class DebugManager {
    private static readonly ANTICIPATION_CONFIG_FOLDER: string = 'Anticipation Config'
    private static readonly BONUS_GAME_FOLDER: string = 'Bonus Game'
    private static readonly REELS_CONFIG_FOLDER: string = 'Reels Time Config'
    private static readonly BIG_WIN_SPINE_PATH = '/assets/animations/commendation/commendations.json'
    private static readonly BIG_WIN_ATLAS_PATH = '/assets/animations/commendation/commendations.atlas'
    private static readonly BIG_WIN_MIN_MULTIPLIER = 10
    private static readonly MEGA_WIN_MIN_MULTIPLIER = 20
    private static readonly SUPER_MEGA_WIN_MIN_MULTIPLIER = 30
    private static readonly ULTRA_WIN_MIN_MULTIPLIER = 50
    static onFreeSpinsTriggerWithWin: VoidFunction
    static onFreeSpinsTrigger: VoidFunction
    static onFreeSpinsCheckScattersLand: VoidFunction
    static onFreeSpinsOnlySuspense: VoidFunction
    static onFreeSpinsNoSuspense: VoidFunction

    public static createFolders(): IFolder[] {
        return [
            {
                name: 'Popups',
                items: [
                    { callback: DebugManager.sessionTimeoutPopup, name: 'Session Timeout' },
                    { callback: DebugManager.hardwareAccelerationPopup, name: 'Hardware Acceleration' },
                    { callback: DebugManager.connectionErrorPopup, name: 'Connection Error' },
                    { callback: DebugManager.commonErrorPopup, name: 'Common Error' },
                    { callback: DebugManager.realityCheckPopup, name: 'Reality Check' },
                    {
                        callback: () => {
                            GameSignals.showFreeSpinsSummary.emit(234)
                        },
                        name: 'FS Outro',
                    },
                    {
                        callback: () => DebugManager.emitFeatureIntro('freespins', 10),
                        name: 'Feature Intro: Free Spins',
                    },
                    {
                        callback: () => DebugManager.emitFeatureIntro('holdandwin'),
                        name: 'Feature Intro: Hold & Win',
                    },

                    //## Max Exposure Popup : It works, but the popup is not shown immediately. It is armed for the next round end.
                    { callback: DebugManager.showMaxExposurePopup, name: 'Max Exposure' },
                    {
                        callback: DebugManager.armMaxExposureForNextRoundEnd,
                        name: 'Max Exposure: arm for next round end',
                    },
                    { callback: DebugManager.showUnfinishedRoundPopup, name: 'Unfinished Round' },
                ],
            },
            {
                name: 'Free Spins',
                items: [
                    { name: '3 Scatters Land + Payline' },
                    { name: '3 Scatters Land' },
                    { name: '2 Scatters Land (2 + 4 reels)' },
                    { name: '2 Scatters Land' },
                    { name: '1 Scatters Land' },
                ],
            },
            {
                name: 'Big Win Tests',
                items: [
                    { callback: DebugManager.testBigWin, name: 'Test BIG WIN: 1_BIG_IN -> 1_BIG_LOOP' },
                    { callback: DebugManager.testMegaWin, name: 'Test MEGA WIN: 2_MEGA_IN -> 2_MEGA_LOOP' },
                    { callback: DebugManager.testSuperMegaWin, name: 'Test ULTRA WIN: 3_ULTRA_IN -> 3_ULTRA_LOOP' },
                    { callback: DebugManager.testUltraWin, name: 'Test ULTRA MEGA WIN: 4_ULTRA_MEGA_IN -> 4_ULTRA_MEGA_LOOP' },
                    { callback: DebugManager.skipBigWin, name: 'Skip Big Win' },
                ],
            },
        ]
    }

    private static testBigWin = async () => {
        await DebugManager.emitBigWinTest(DebugManager.BIG_WIN_MIN_MULTIPLIER, 0)
    }

    private static testMegaWin = async () => {
        await DebugManager.emitBigWinTest(DebugManager.MEGA_WIN_MIN_MULTIPLIER, 1)
    }

    private static testSuperMegaWin = async () => {
        await DebugManager.emitBigWinTest(DebugManager.SUPER_MEGA_WIN_MIN_MULTIPLIER, 2)
    }

    private static testUltraWin = async () => {
        await DebugManager.emitBigWinTest(DebugManager.ULTRA_WIN_MIN_MULTIPLIER, 3)
    }

    private static skipBigWin = () => {
        GameSignals.skipBigWin.emit()
    }

    //## Show max exposure popup immediately. It is not the real path, but it is useful for testing the popup.
    private static showMaxExposurePopup = () => {
        ;(GameSignals as any).showMaxExposure?.emit()
    }

    //## Unfinished Popup..
    private static showUnfinishedRoundPopup = () => {
        ;(GameSignals as any).unfinishedRoundRequested?.emit()
    }

    private static armMaxExposureForNextRoundEnd = () => {
        const model = Facade.model as any

        if (!model?.spinData) {
            console.warn('[DebugManager] No spinData yet — spin once before arming Max Exposure.')
            return
        }

        model.spinData.cap_amount_reached = true
        model.isMaxExposureShown = false
        applyMaxWinCapToSpinData(model)

        console.log('[DebugManager] Max Exposure armed — the round stops and the popup shows at its end.')
    }

    private static async emitBigWinTest(multiplier: number, level: number): Promise<void> {
        const isReady = await DebugManager.ensureBigWinResourcesReady()
        if (!isReady) {
            console.warn('[DebugManager] BigWin resources are not ready yet. Retry in a moment.')
            return
        }

        const bet = DebugManager.getBigWinDebugBet()
        const minBigWinAmount = Number(Facade.winningsConfig?.MIN_BIG_WIN_AMOUNT ?? 0)
        const targetAmount = Math.max(bet * multiplier, minBigWinAmount)

        GameSignals.bigWinPresentation.emit({
            win: targetAmount,
            bet,
            bigWinLevel: level,
        })
    }

    private static async ensureBigWinResourcesReady(): Promise<boolean> {
        try {
            const hasBigWin = Boolean(PIXI.Assets.get('bigWinAnimation'))

            if (hasBigWin) {
                return true
            }

            const bigWinSpinePath = buildAssetUrl(DebugManager.BIG_WIN_SPINE_PATH)
            const bigWinAtlasPath = buildAssetUrl(DebugManager.BIG_WIN_ATLAS_PATH)

            PIXI.Assets.add({ alias: 'bigWinAnimation', src: bigWinSpinePath })
            PIXI.Assets.add({ alias: 'bigWinAnimation.atlas', src: bigWinAtlasPath })

            await PIXI.Assets.load('bigWinAnimation')

            return Boolean(PIXI.Assets.get('bigWinAnimation'))
        } catch (error) {
            console.error('[DebugManager] Failed to preload BigWin resources', error)
            return false
        }
    }

    private static getBigWinDebugBet(): number {
        const bet = Number(Facade.model?.currentBaseBet ?? Facade.model?.currentBet ?? 1)
        return Number.isFinite(bet) && bet > 0 ? bet : 1
    }

    public static registerSuspenseConfig(): void {
        const folder = DebugManager.findDebugFolder(DebugManager.ANTICIPATION_CONFIG_FOLDER)

        const anticipationConfig: any = Facade['anticipationConfig' as keyof typeof Facade]

        if (folder != null && anticipationConfig) {
            folder.add(anticipationConfig, 'ANTICIPATION_REEL_TIME', 0, 3, 0.01).name('Anticipation Reel Time')
            folder
                .add(anticipationConfig, 'NON_WINNINGS_SYMBOLS_STOPPING_SPEED', 0, 3, 0.01)
                .name('Non Winnings Symbols Stopping Speed')
            folder
                .add(anticipationConfig, 'DELAY_PER_NON_WINNINGS_REEL_SYMBOL', 0, 3, 0.01)
                .name('Delay Per Non Winnings Symbols')
            folder.add(anticipationConfig, 'NON_WINNINGS_BOUNCING_SHIFT', 0, 100, 1).name('Non Winnings Bouncing Shift')
            folder
                .add(anticipationConfig, 'NON_WINNINGS_BOUNCING_BACK_TIME', 0, 1, 0.01)
                .name('Non Winnings Bouncing Back Time')
            folder
                .add(anticipationConfig, 'DELAY_BETWEEN_STAGE1_AND_STAGE2', 0, 3, 0.01)
                .name('Delay Between Stage1 And Stage2')
            folder
                .add(anticipationConfig, 'WINNINGS_SYMBOLS_STOPPING_SPEED', 0, 3, 0.01)
                .name('Winnings Symbols Stopping Speed')
            folder
                .add(anticipationConfig, 'DELAY_PER_WINNINGS_REEL_SYMBOL', 0, 3, 0.01)
                .name('Delay Per Winnings Reel Symbol')
            folder.add(anticipationConfig, 'WINNINGS_BOUNCING_SHIFT', 0, 100, 1).name('Winnings Bouncing Shift')
            folder
                .add(anticipationConfig, 'WINNINGS_BOUNCING_BACK_TIME', 0, 1, 0.01)
                .name('Winnings Bouncing Back Times')
        }
    }

    public static registerMultiplierConfig(): void {
        const debugModule = DebugManager.findDebugModule()

        if (debugModule != null && (debugModule as any).panel) {
            const folder = debugModule.createFolder('Multiplier Feature Timings')
            folder
                .add(Facade.reelsConfig, 'DELAY_PER_MULTIPLIER_SYMBOLS', 0, 3, 0.01)
                .name('Delay per multiplier symbols')
            folder
                .add(Facade.reelsConfig, 'DELAY_BEFORE_TRAIL_ANIMATION', 0, 3, 0.01)
                .name('Delay before trail animation')
        }
    }

    public static registerReelsConfig(): void {
        const debugModule = DebugManager.findDebugModule()

        if (debugModule != null && (debugModule as any).panel) {
            const folder = DebugManager.findDebugFolder(DebugManager.REELS_CONFIG_FOLDER)
            DebugManager.initReelsConfigFolder(folder, debugModule)
        }
    }

    public static initReelsConfigFolder(folder: any, debugModule: DebugModule): any {
        if (folder == null) {
            folder = debugModule.createFolder('Reels Time Config')
        }

        const reelsConfig: any = Facade['reelsConfig' as keyof typeof Facade]

        if (reelsConfig && reelsConfig.NORMAL) {
            const normalFolder = folder.addFolder('Normal Spin Settings')

            normalFolder.add(reelsConfig.NORMAL, 'START_BOUNCING_SHIFT', 0, 100, 1).name('Start Bounce Shift')
            normalFolder.add(reelsConfig.NORMAL, 'START_BOUNCING_BACK_TIME', 0, 1, 0.01).name('Start Bounce Back Time')
            normalFolder.add(reelsConfig.NORMAL, 'REGULAR_SPEED', 0, 5000, 1).name('Spinning Time')
            normalFolder.add(reelsConfig.NORMAL, 'STOPPING_SPEED', 0, 5000, 1).name('Stopping Time')
            normalFolder.add(reelsConfig.NORMAL, 'BOUNCING_SHIFT', 0, 100, 1).name('Bounce Shift')
            normalFolder.add(reelsConfig.NORMAL, 'BOUNCING_BACK_TIME', 0, 1, 0.01).name('Bounce Back Time')
            normalFolder
                .add(reelsConfig.NORMAL, 'DELAY_BETWEEN_REELS_ON_START', 0, 1, 0.01)
                .name('Delay Between Reels On Start')
            normalFolder
                .add(reelsConfig.NORMAL, 'DELAY_BETWEEN_REELS_ON_STOPPING', 0, 1, 0.01)
                .name('Delay Between Reels On Stopping')
            normalFolder.add(reelsConfig.NORMAL, 'DELAY_PER_REEL_SYMBOL', 0, 1, 0.01).name('Delay Per Reel Symbol')
            normalFolder.add(reelsConfig.NORMAL, 'DELAY_BEFORE_STOPPING', 0, 3, 0.01).name('Delay Before Stopping')
            normalFolder
                .add(reelsConfig.NORMAL, 'DELAY_BETWEEN_CASCADE_REELS', 0, 1, 0.01)
                .name('Delay Between Cascade Reels')
            normalFolder
                .add(reelsConfig, 'SCATTER_SUSPENSE_TIME', 0, 6, 0.05)
                .name('Scatter Suspense Time')
            normalFolder
                .add(reelsConfig, 'FEATURE_SUSPENSE_TIME', 0, 6, 0.05)
                .name('Feature Suspense Time')
        }

        if (reelsConfig && reelsConfig.QUICK) {
            const quickFolder = folder.addFolder('Quick Spin Settings')

            quickFolder.add(reelsConfig.QUICK, 'START_BOUNCING_SHIFT', 0, 100, 1).name('Start Bounce Shift')
            quickFolder.add(reelsConfig.QUICK, 'START_BOUNCING_BACK_TIME', 0, 1, 0.01).name('Start Bounce Back Time')
            quickFolder.add(reelsConfig.QUICK, 'REGULAR_SPEED', 0, 5000, 1).name('Spinning Time')
            quickFolder.add(reelsConfig.QUICK, 'STOPPING_SPEED', 0, 5000, 1).name('Stopping Time')
            quickFolder.add(reelsConfig.QUICK, 'BOUNCING_SHIFT', 0, 100, 1).name('Bounce Shift')
            quickFolder.add(reelsConfig.QUICK, 'BOUNCING_BACK_TIME', 0, 1, 0.01).name('Bounce Back Time')
            quickFolder
                .add(reelsConfig.QUICK, 'DELAY_BETWEEN_REELS_ON_START', 0, 1, 0.01)
                .name('Delay Between Reels On Start')
            quickFolder
                .add(reelsConfig.QUICK, 'DELAY_BETWEEN_REELS_ON_STOPPING', 0, 1, 0.01)
                .name('Delay Between Reels On Stopping')
            quickFolder.add(reelsConfig.QUICK, 'DELAY_PER_REEL_SYMBOL', 0, 1, 0.01).name('Delay Per Reel Symbol')
            quickFolder.add(reelsConfig.QUICK, 'DELAY_BEFORE_STOPPING', 0, 3, 0.01).name('Delay Before Stopping')
            quickFolder
                .add(reelsConfig.QUICK, 'DELAY_BETWEEN_CASCADE_REELS', 0, 1, 0.01)
                .name('Delay Between Cascade Reels')
        }

        return folder
    }

    public static findDebugFolder(title: string): any {
        const debugModule = DebugManager.findDebugModule()

        if (debugModule != null && (debugModule as any).panel && (debugModule as any).panel.folders) {
            for (const folder of debugModule.panel.folders) {
                if (folder._title === title) {
                    return folder
                }
            }
        }

        return null
    }

    private static findDebugModule(): DebugModule {
        if (ModulesManager.isModuleEnabled('debugPanel')) {
            return ModulesManager.getModule('debugPanel') as DebugModule
        }

        return null
    }

    private static sessionTimeoutPopup() {
        GameSignals.sessionTimeout.emit(null)
    }

    private static hardwareAccelerationPopup() {
        GameSignals.hardwareAcceleration.emit()
    }

    private static connectionErrorPopup() {
        GameSignals.errorConnection.emit()
    }

    private static commonErrorPopup() {
        GameSignals.commonError.emit()
    }

    private static realityCheckPopup() {
        GameSignals.realityCheck2.emit()
    }

    private static emitFeatureIntro(kind: 'freespins' | 'holdandwin', freeSpinsCount?: number): void {
        console.log(`[DebugManager] Triggering feature intro popup: ${kind}`, { freeSpinsCount });
        (GameSignals as any).featureIntroRequested?.emit({ kind, freeSpinsCount })
    }
}
